import React, { useEffect, useState } from 'react';
import { makeStyles, Text, Badge, Input, Spinner, Button } from '@fluentui/react-components';
import { SearchRegular, CalendarRegular, ArrowDownloadRegular } from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../../components/layout';
import { PermissionGate, SearchableDropdown, TablePagination } from '../../../components/common';
import {
  ApiClient,
  getErrorMessage,
  newToast,
  fullDateTime,
} from '../../../configurations/index.config';
import type { CheckpointScan, Site, Employee, PaginationMeta } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#faf9f8',
    borderRadius: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  tableHeader: {
    backgroundColor: '#faf9f8',
    borderBottom: '1px solid #edebe9',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #edebe9',
    ':hover': {
      backgroundColor: '#f3f2f1',
    },
  },
  td: {
    padding: '12px 16px',
    color: '#323130',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px',
  },
  emptyStateIcon: {
    fontSize: '48px',
    color: '#c8c6c4',
    marginBottom: '16px',
  },
});

const CheckpointScansFluentUI: React.FC = () => {
  const styles = useStyles();

  const [loading, setLoading] = useState(true);
  const [scans, setScans] = useState<CheckpointScan[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(undefined);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchSites();
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchScans();
  }, [
    currentPage,
    itemsPerPage,
    selectedSiteId,
    selectedEmployeeId,
    selectedStatus,
    startDate,
    endDate,
  ]);

  const fetchSites = async () => {
    try {
      const response = await ApiClient.get('/get-sites');
      if (response.data.status === 'success') {
        setSites(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await ApiClient.get('/get-employee-data');
      if (response.data.status === 'success') {
        setEmployees(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchScans = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (selectedSiteId) {
        params.siteId = selectedSiteId;
      }
      if (selectedEmployeeId) {
        params.employeeId = selectedEmployeeId;
      }
      if (selectedStatus !== 'all') {
        params.validationStatus = selectedStatus;
      }
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const response = await ApiClient.get('/get-checkpoint-scans', { params });

      if (response.data.status === 'success') {
        setScans(response.data.data || []);
        setPagination(response.data.pagination);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, any> = {
      valid: { color: 'success', label: 'Valid' },
      invalid_location: { color: 'danger', label: 'Invalid Location' },
      invalid_time: { color: 'warning', label: 'Invalid Time' },
      manual_override: { color: 'informative', label: 'Manual Override' },
    };

    const config = colors[status] || { color: 'subtle', label: status };

    return (
      <Badge appearance="tint" color={config.color}>
        {config.label}
      </Badge>
    );
  };

  const getScanSourceBadge = (source: string) => {
    const icons: Record<string, string> = {
      camera: '📷',
      manual: '✏️',
      override: '🔓',
    };

    return (
      <Badge appearance="outline" color="brand">
        {icons[source] || ''} {source}
      </Badge>
    );
  };

  return (
    <PermissionGate privilege="checkpoint-scans.view" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <Text className={styles.pageTitle}>Checkpoint Scans</Text>
          <Text className={styles.pageSubtitle}>
            View guard patrol checkpoint scan history with GPS validation
          </Text>
        </div>

        <ContentCard>
          <TableCommandBar
            title={`Scan History (${pagination?.totalItems || 0})`}
            actions={
              <Button appearance="secondary" icon={<ArrowDownloadRegular />}>
                Export
              </Button>
            }
          />

          <div className={styles.filters}>
            <Input
              type="date"
              contentBefore={<CalendarRegular />}
              placeholder="Start Date"
              value={startDate}
              onChange={e => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
            />

            <Input
              type="date"
              contentBefore={<CalendarRegular />}
              placeholder="End Date"
              value={endDate}
              onChange={e => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
            />

            <SearchableDropdown
              placeholder="Filter by Site"
              selectedValue={selectedSiteId ? selectedSiteId.toString() : 'all'}
              onSelect={value => {
                setSelectedSiteId(value === 'all' ? undefined : Number(value));
                setCurrentPage(1);
              }}
              options={[
                { id: 'all', label: 'All Sites' },
                ...sites.map(site => ({
                  id: site.id.toString(),
                  label: site.name,
                })),
              ]}
            />

            <SearchableDropdown
              placeholder="Filter by Employee"
              selectedValue={selectedEmployeeId ? selectedEmployeeId.toString() : 'all'}
              onSelect={value => {
                setSelectedEmployeeId(value === 'all' ? undefined : Number(value));
                setCurrentPage(1);
              }}
              options={[
                { id: 'all', label: 'All Employees' },
                ...employees.map(emp => ({
                  id: emp.id.toString(),
                  label: `${emp.first_name} ${emp.surname}`,
                })),
              ]}
            />

            <SearchableDropdown
              placeholder="Filter by Status"
              selectedValue={selectedStatus}
              onSelect={value => {
                setSelectedStatus(value);
                setCurrentPage(1);
              }}
              options={[
                { id: 'all', label: 'All Status' },
                { id: 'valid', label: 'Valid' },
                { id: 'invalid_location', label: 'Invalid Location' },
                { id: 'invalid_time', label: 'Invalid Time' },
                { id: 'manual_override', label: 'Manual Override' },
              ]}
            />
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
              <Spinner label="Loading scan history..." />
            </div>
          ) : scans.length === 0 ? (
            <div className={styles.emptyState}>
              <SearchRegular className={styles.emptyStateIcon} />
              <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                No scans found
              </Text>
              <Text style={{ color: '#605e5c' }}>Try adjusting your filters or date range</Text>
            </div>
          ) : (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.th}>Timestamp</th>
                  <th className={styles.th}>Checkpoint</th>
                  <th className={styles.th}>Site</th>
                  <th className={styles.th}>Client</th>
                  <th className={styles.th}>Employee</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Source</th>
                </tr>
              </thead>
              <tbody>
                {scans.map(scan => (
                  <tr key={scan.id} className={styles.tr}>
                    <td className={styles.td}>
                      <Text size={300}>{fullDateTime(scan.scan_timestamp)}</Text>
                    </td>
                    <td className={styles.td}>
                      <Text weight="semibold">{scan.checkpoint_name}</Text>
                    </td>
                    <td className={styles.td}>
                      <Text>{scan.site_name || '—'}</Text>
                    </td>
                    <td className={styles.td}>
                      <Text>{scan.client_name || '—'}</Text>
                    </td>
                    <td className={styles.td}>
                      <Text>{scan.employee_name || '—'}</Text>
                    </td>
                    <td className={styles.td}>{getStatusBadge(scan.validation_status)}</td>
                    <td className={styles.td}>{getScanSourceBadge(scan.scan_source)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && pagination && scans.length > 0 && (
            <TablePagination
              totalItems={pagination.totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default CheckpointScansFluentUI;
